import { AnimatePresence, motion } from 'framer-motion';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { CiSquareMinus, CiSquarePlus } from 'react-icons/ci';
import { IoChevronDownSharp } from 'react-icons/io5';
import { DimensionFieldsModel } from '../../_core/_model.ts';
import { GetDimensionFields, GetDimensionFieldValues } from '../../_core/_request.ts';
import { fieldType, OPERATOR_LABELS, Separators } from '../../Constants.ts';
import { ThemeContext } from '../../context/ThemeContext.js';
import DateConrol from '../../UIComponents/DateConrol.tsx';
import DropdownWithSearch from '../../UIComponents/DropdownWithSearch.tsx';
import MultiSelectDropdown, { OptionType } from '../../UIComponents/MultiSelectDropdownProps.tsx';
import DimensionFields from '../Tools/DimensionFields.tsx';
import TooltipButton from '../Tools/TooltipButton.tsx';
import { RoleCondition } from './_core/_model.ts';

interface request {
    myConditions: RoleCondition[],
    dataMapCode: string,
    updateRules: (conditions: RoleCondition[]) => void
}

interface oType {
    key: string,
    text: string
}

const operatorsForType: { [key: string]: oType[] } = {
    0: [{
        key: "=", text: "="
    },
    {
        key: "like", text: "like"
    },
    {
        key: "startwith", text: "Start with"
    },
    {
        key: "endwith", text: "End with"
    }
    ],
    1: [{
        key: "=", text: "="
    },
    {
        key: "in", text: "In"
    },
    {
        key: "not in", text: "Not in"
    }
    ],
    2: [{
        key: "=", text: "="
    }],
}

interface rowMetaModel {
    field?: DimensionFieldsModel,
    options: string[],
    isLoading?: boolean // <-- Added to accurately track backend loading state
}

const VISUAL_SEPARATOR = ", ";

export function FilterConditionBuilder({ dataMapCode, myConditions, updateRules }: request) {
    const [items, setItems] = useState<DimensionFieldsModel[]>([]);
    const [rowMeta, setRowMeta] = useState<rowMetaModel[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [conditions, setConditions] = useState<RoleCondition[]>([]);
    const { themeConfig } = useContext(ThemeContext);
    const fieldValuesCache = useRef<Record<string, Promise<string[]>>>({});

    const fetchFieldOptions = async (columnName: string, mapCode: string): Promise<string[]> => {
        const cacheKey = `${mapCode}_${columnName}`;

        if (!fieldValuesCache.current[cacheKey]) {
            fieldValuesCache.current[cacheKey] = GetDimensionFieldValues({ columnName, forDimension: mapCode })
                .then(res => res.data || [])
                .catch(err => {
                    // Clear the cache on failure so the user can try again later
                    delete fieldValuesCache.current[cacheKey];
                    console.error("Failed to fetch options for", columnName, err);
                    return [];
                });
        }
        return fieldValuesCache.current[cacheKey];
    };

    // Track initialization to prevent keystrokes from resetting the table
    const initializedDataMap = useRef<string | null>(null);

    const getNewRow = () => {
        return {
            selected: false,
            id: Date.now() + Math.random(),
            dataMapColumn: "",
            oper: "=",
            value: "",
            andOr: "Or"
        }
    }

    const getOptions = (vArr: string[]) => {
        let op: OptionType[] = [];
        for (var indx in vArr) {
            op.push({
                key: vArr[indx],
                value: vArr[indx]
            })
        }
        return op;
    }

    // Function to filter out values that are already selected in other rows for the same column
    const getAvailableOptions = (allOptions: string[] | undefined, currentRowIndex: number, columnName: string) => {
        if (!allOptions) return [];

        const usedValues = new Set<string>();

        conditions.forEach((rule, idx) => {
            if (idx !== currentRowIndex && rule.dataMapColumn === columnName && rule.value) {
                const vals = String(rule.value).split(Separators.in);
                vals.forEach(v => usedValues.add(v.trim()));
            }
        });

        return allOptions.filter(opt => !usedValues.has(opt));
    }

    useEffect(() => {
        const loadInitialData = async () => {
            if (!dataMapCode) return;

            // Only show full table loading when fetching a new dataMapCode
            setLoading(true);

            try {
                const fieldsRes = await GetDimensionFields(dataMapCode);
                const fetchedFields = fieldsRes.data;
                setItems(fetchedFields);

                const metaPromises = myConditions.map(async (row) => {
                    const fld = fetchedFields.find((x: DimensionFieldsModel) => x.columnName === row.dataMapColumn);
                    if (fld && fld.fieldType === fieldType.dropdown) {
                        try {
                            // --- NEW: Use the cache helper ---
                            const options = await fetchFieldOptions(fld.columnName, dataMapCode);
                            return { field: fld, options: options, isLoading: false };
                        } catch (e) {
                            return { field: fld, options: [], isLoading: false };
                        }
                    }
                    return { field: fld, options: [], isLoading: false };
                });

                const resolvedMeta = await Promise.all(metaPromises);
                setRowMeta(resolvedMeta);

                // --- CASE NORMALIZATION FOR MIGRATED DATA ---
                const normalizedConditions = myConditions.map((rule, idx) => {
                    // 1. Normalize Operator (e.g., "IN" -> "in", "LIKE" -> "like")
                    let newOper = rule.oper ? String(rule.oper).toLowerCase() : "=";

                    // 2. Normalize And/Or (e.g., "AND" -> "And", "or" -> "Or")
                    let newAndOr = rule.andOr;
                    if (newAndOr) {
                        newAndOr = String(newAndOr).toLowerCase() === 'or' ? 'Or' : 'And';
                    }

                    // 3. Normalize Values (Match backend case with fetched options case)
                    let newValue = rule.value;
                    const meta = resolvedMeta[idx];

                    if (newValue && meta && meta.options && meta.options.length > 0) {
                        const strValue = String(newValue);

                        if (newOper === 'in' || newOper === 'not in') {
                            // Fix multi-select values case
                            const parts = strValue.split(Separators.in);
                            const correctedParts = parts.map(p => {
                                const trimmed = p.trim();
                                const matchedOpt = meta.options.find(opt => opt.toLowerCase() === trimmed.toLowerCase());
                                return matchedOpt || trimmed; // Use exact matched case, or fallback to original
                            });
                            newValue = correctedParts.join(Separators.in);
                        } else {
                            // Fix single-select values case
                            const matchedOpt = meta.options.find(opt => opt.toLowerCase() === strValue.trim().toLowerCase());
                            if (matchedOpt) {
                                newValue = matchedOpt;
                            }
                        }
                    }

                    return {
                        ...rule,
                        oper: newOper,
                        andOr: newAndOr,
                        value: newValue
                    };
                });

                // Set the corrected conditions into state
                setConditions(normalizedConditions);

                // Optional but recommended: sync the corrected data back to the parent 
                // so that the next save to the DB uses the proper casing.
                updateRules(normalizedConditions);

                initializedDataMap.current = dataMapCode;
            } catch (err) {
                console.error("Failed to fetch initial conditions data:", err);
            } finally {
                setLoading(false);
            }
        };

        // Only run this heavy fetch if dataMapCode changed, OR if it's the first time parent passed down conditions
        if (dataMapCode !== initializedDataMap.current || (conditions.length === 0 && myConditions.length > 0)) {
            loadInitialData();
        }
        // Safely ignore myConditions keystroke updates!
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataMapCode, myConditions.length]);

    const checkAndUpdateMeta = async (rowIndex: number, fieldName: string) => {
        const fld = items.find((x) => x.columnName === fieldName);
        if (!fld) return;

        // Set localized row loading state to true immediately
        setRowMeta(prev => {
            const newMeta = [...prev];
            newMeta[rowIndex] = { field: fld, options: [], isLoading: true };
            return newMeta;
        });

        if (fld.fieldType === fieldType.dropdown) {
            try {
                // --- NEW: Use the cache helper ---
                const options = await fetchFieldOptions(fieldName, dataMapCode);

                setRowMeta(prev => {
                    const newMeta = [...prev];
                    newMeta[rowIndex] = { field: fld, options: options, isLoading: false };
                    return newMeta;
                });
            } catch (e) {
                setRowMeta(prev => {
                    const newMeta = [...prev];
                    newMeta[rowIndex] = { field: fld, options: [], isLoading: false };
                    return newMeta;
                });
            }
        } else {
            setRowMeta(prev => {
                const newMeta = [...prev];
                newMeta[rowIndex] = { field: fld, options: [], isLoading: false };
                return newMeta;
            });
        }
    }

    const updateConditions = (cArr: RoleCondition[]) => {
        setConditions(cArr);
        updateRules(cArr);
    }

    const handleFilterRuleChange = (id: string | number, field: string, value: any) => {
        const updatedConditions = conditions.map(rule =>
            rule.id === id ? { ...rule, [field]: value } : rule
        );
        updateConditions(updatedConditions);
    };

    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = e.currentTarget.checked;
        const updatedConditions = conditions.map(x => ({ ...x, selected: isChecked }));
        updateConditions(updatedConditions);
    };

    const getOperators = (columnName: string): oType[] => {
        if (typeof columnName !== 'undefined') {
            let tField = items.find((x) => x.columnName === columnName)
            if (typeof tField !== 'undefined')
                return operatorsForType[tField.fieldType] || [];
        }
        return [];
    }

    return (
        <div className="w-100 rounded" style={{ border: `1px solid ${themeConfig?.colors?.border || 'var(--bs-border-color)'}` }}>
            <table
                className="table mb-0"
                style={{
                    '--bs-table-bg': 'transparent',
                    '--bs-table-color': 'inherit',
                    '--bs-table-border-color': themeConfig?.colors?.border || 'inherit'
                } as React.CSSProperties}
            >
                <thead>
                    <tr>
                        <th className='text-center align-middle' style={{ width: '40px', borderBottomWidth: '2px' }}>
                            <input
                                checked={conditions.length > 0 && conditions.every((x) => x.selected)}
                                type='checkbox'
                                className="form-check-input"
                                style={{ width: "18px", height: "18px", marginTop: 0 }}
                                onChange={handleSelectAll}
                            />
                        </th>
                        <th className='fw-normal' style={{ fontSize: "12px", width: "300px", borderBottomWidth: '2px' }}>*DataMap Column</th>
                        <th className='fw-normal' style={{ width: "120px", fontSize: "12px", borderBottomWidth: '2px' }}>*Operator</th>
                        <th className='fw-normal' style={{ fontSize: "12px", borderBottomWidth: '2px' }}>Value</th>
                        <th className='fw-normal' style={{ width: "100px", fontSize: "12px", borderBottomWidth: '2px' }}>And/Or</th>
                        <th style={{ width: "70px", borderBottomWidth: '2px' }}></th>
                    </tr>
                </thead>
                <tbody>
                    {(dataMapCode == "" || dataMapCode == null) ? <tr>
                        <td colSpan={6} className="text-center py-5 border-bottom-0">
                            <span className="ms-2" style={{ color: 'inherit', opacity: 0.7 }}>Select a Dimension to Continue</span>
                        </td>
                    </tr> : loading ? (
                        <tr>
                            <td colSpan={6} className="text-center py-5 border-bottom-0">
                                <Spinner animation="border" variant="secondary" size="sm" />
                                <span className="ms-2" style={{ color: 'inherit', opacity: 0.7 }}>Loading conditions...</span>
                            </td>
                        </tr>
                    ) : (
                        <AnimatePresence>
                            {conditions.map((rule, rowIndx) => {
                                // Calculate dynamically available options for this specific row based on other rows' selections
                                const availableOptions = getAvailableOptions(rowMeta[rowIndx]?.options, rowIndx, rule.dataMapColumn);

                                return (
                                    <motion.tr
                                        key={rule.id}
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 10 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <td className='text-center align-middle border-bottom'>
                                            <input
                                                checked={rule.selected || false}
                                                onChange={(e) => handleFilterRuleChange(rule.id, 'selected', e.currentTarget.checked)}
                                                type='checkbox'
                                                className="form-check-input"
                                                style={{ width: "18px", height: "18px", marginTop: 0 }}
                                            />
                                        </td>
                                        <td className='align-middle border-bottom'>
                                            <DimensionFields
                                                iniVal={rule.dataMapColumn}
                                                items={items}
                                                loading={false}
                                                onChange={(value) => {
                                                    const updatedConditions = conditions.map((c, i) =>
                                                        i === rowIndx ? { ...c, dataMapColumn: value, value: "" } : c
                                                    );
                                                    updateConditions(updatedConditions);
                                                    checkAndUpdateMeta(rowIndx, value);
                                                }}
                                            />
                                        </td>
                                        <td className='align-middle border-bottom'>
                                            <select
                                                className="form-select w-100"
                                                style={{ backgroundColor: 'transparent', color: 'inherit', fontWeight: 500 }}
                                                value={rule.oper}
                                                onChange={(e) => handleFilterRuleChange(rule.id, 'oper', e.target.value)}
                                            >
                                                {getOperators(rule.dataMapColumn).map((v) => (
                                                    <option key={v.key} value={v.key}>
                                                        {OPERATOR_LABELS[v.key] || v.key}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                        <td className='align-middle border-bottom'>
                                            {rowMeta[rowIndx]?.field?.fieldType === fieldType.dropdown ?
                                                ((rule.oper === "in" || rule.oper === "not in") ?
                                                    <MultiSelectDropdown
                                                        className="w-100"
                                                        label={undefined}
                                                        maxWidth="100%"
                                                        icon={undefined}
                                                        options={getOptions(availableOptions)}
                                                        // Parse incoming value using INTERNAL_SEPARATOR
                                                        selected={(rule.value == null || rule.value === "") ? [] : rule.value.split(Separators.in)}
                                                        // Save value using INTERNAL_SEPARATOR
                                                        onChange={(v) => handleFilterRuleChange(rule.id, 'value', v.join(Separators.in))}
                                                        initiator={(clickCall) => (
                                                            <div
                                                                className="form-control d-flex align-items-center justify-content-between w-100"
                                                                style={{ backgroundColor: 'transparent', color: 'inherit', fontWeight: 500, cursor: 'pointer' }}
                                                                onClick={clickCall}
                                                            >
                                                                <div style={{ flex: 1, minWidth: 0 }} className="text-start pe-2 text-truncate">
                                                                    {/* Map the internal separator to visual commas for Tooltip and Label */}
                                                                    <TooltipButton
                                                                        truncate={true}
                                                                        title={rule.value ? rule.value.split(Separators.in).join(VISUAL_SEPARATOR) : "Select values..."}
                                                                    >
                                                                        <span className="text-truncate d-block w-100">
                                                                            {rule.value ? (
                                                                                <span>{rule.value.split(Separators.in).join(VISUAL_SEPARATOR)}</span>
                                                                            ) : (
                                                                                <span style={{ opacity: 0.6, fontWeight: 'normal' }}>Select values...</span>
                                                                            )}
                                                                        </span>
                                                                    </TooltipButton>
                                                                </div>
                                                                <IoChevronDownSharp size={16} className="flex-shrink-0" />
                                                            </div>
                                                        )}
                                                    /> :
                                                    (typeof rowMeta[rowIndx] !== 'undefined' && !rowMeta[rowIndx].isLoading ?
                                                        <DropdownWithSearch
                                                            className="w-100"
                                                            label="Select a value"
                                                            maxWidth="100%"
                                                            options={getOptions(availableOptions)}
                                                            value={rule.value}
                                                            onChange={(v) => handleFilterRuleChange(rule.id, 'value', v || "")}
                                                            initiator={(clickCall, selectedOption) => (
                                                                <div
                                                                    className="form-control d-flex align-items-center justify-content-between w-100"
                                                                    style={{ backgroundColor: 'transparent', color: 'inherit', fontWeight: 500, cursor: 'pointer' }}
                                                                    onClick={clickCall}
                                                                >
                                                                    <div style={{ flex: 1, minWidth: 0 }} className="text-start pe-2 text-truncate">
                                                                        <TooltipButton truncate={true} title={selectedOption ? selectedOption.value : "Select a value"}>
                                                                            <span className="text-truncate d-block w-100">
                                                                                {selectedOption ? (
                                                                                    <span>{selectedOption.value}</span>
                                                                                ) : (
                                                                                    <span style={{ opacity: 0.6, fontWeight: 'normal' }}>Select a value</span>
                                                                                )}
                                                                            </span>
                                                                        </TooltipButton>
                                                                    </div>
                                                                    <IoChevronDownSharp size={16} className="flex-shrink-0" />
                                                                </div>
                                                            )}
                                                        /> :
                                                        <div className="d-flex align-items-center gap-2 form-control w-100" style={{ backgroundColor: 'transparent', color: 'inherit' }}>
                                                            <Spinner size="sm" animation="border" />
                                                            <span style={{ fontSize: '14px', opacity: 0.7 }}>Loading...</span>
                                                        </div>
                                                    )
                                                ) :
                                                rowMeta[rowIndx]?.field?.fieldType === fieldType.date ?
                                                    <DateConrol
                                                        value={rule.value}
                                                        onChange={(date) => handleFilterRuleChange(rule.id, 'value', date)}
                                                    />
                                                    :
                                                    <input
                                                        type="text"
                                                        className="form-control w-100"
                                                        style={{ backgroundColor: 'transparent', color: 'inherit', fontWeight: 500 }}
                                                        placeholder="Enter value"
                                                        value={rule.value}
                                                        onChange={(e) => handleFilterRuleChange(rule.id, 'value', e.target.value)}
                                                    />
                                            }
                                        </td>
                                        <td className='align-middle border-bottom'>
                                            <select
                                                value={rule.andOr}
                                                onChange={(e) => handleFilterRuleChange(rule.id, 'andOr', e.target.value)}
                                                className="form-select w-100"
                                                style={{ backgroundColor: 'transparent', color: 'inherit', fontWeight: 500 }}
                                            >
                                                <option value=""></option>
                                                <option value="And">AND</option>
                                                <option value="Or">OR</option>
                                            </select>
                                        </td>
                                        <td className='align-middle border-bottom'>
                                            <div className='d-flex flex-row align-items-center justify-content-center gap-1'>
                                                <TooltipButton title="Add a row">
                                                    <CiSquarePlus
                                                        size={24}
                                                        color={conditions.length > 0 ? themeConfig.colors.success : themeConfig.colors.background}
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => {
                                                            const newRow = getNewRow();
                                                            const updatedConditions = [...conditions];
                                                            updatedConditions.splice(rowIndx + 1, 0, newRow);
                                                            updateConditions(updatedConditions);

                                                            setRowMeta(prev => {
                                                                const newMeta = [...prev];
                                                                newMeta.splice(rowIndx + 1, 0, { field: undefined, options: [], isLoading: false });
                                                                return newMeta;
                                                            });
                                                        }}
                                                    />
                                                </TooltipButton>
                                                <TooltipButton title={conditions.length > 1 ? "Delete row" : "Cannot delete this row"}>
                                                    <CiSquareMinus
                                                        size={24}
                                                        color={conditions.length > 1 ? themeConfig.colors.danger : themeConfig.colors.background}
                                                        style={{ cursor: conditions.length > 1 ? 'pointer' : 'not-allowed' }}
                                                        onClick={() => {
                                                            if (conditions.length > 1) {
                                                                const updatedConditions = conditions.filter((_, i) => i !== rowIndx);
                                                                updateConditions(updatedConditions);

                                                                setRowMeta(prev => prev.filter((_, i) => i !== rowIndx));
                                                            }
                                                        }}
                                                    />
                                                </TooltipButton>
                                            </div>
                                        </td>
                                    </motion.tr>
                                )
                            })}
                        </AnimatePresence>
                    )}
                </tbody>
            </table>
        </div>
    )
}

export default FilterConditionBuilder;